import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { UnpublishFactKind, UnpublishFactRow } from './unpublishFacts';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import styles from './SMLUnpublishList.module.scss';

interface SMLUnpublishListProps {
  rows: UnpublishFactRow[];
  shareUrl: string | null;
  releaseDate: string | null;
}

/** What is true of the page while it is offline, itemised and unvarnished. */
const SMLUnpublishListComponent: React.FC<SMLUnpublishListProps> = ({ rows, shareUrl, releaseDate }) => {
  const intl = useIntl();

  const titleFor = (row: UnpublishFactRow): string => {
    switch (row.kind) {
      case UnpublishFactKind.offline:
        return intl.formatMessage({
          id: 'app.sml.unpublish.fact.offline.title',
          defaultMessage: 'The link stops working',
        });
      case UnpublishFactKind.address:
        return intl.formatMessage({
          id: 'app.sml.unpublish.fact.address.title',
          defaultMessage: 'The address is released',
        });
      case UnpublishFactKind.presaves:
        return intl.formatMessage(
          {
            id: 'app.sml.unpublish.fact.presaves.title',
            defaultMessage: '{count, plural, one {# pre-save still delivers} other {# pre-saves still deliver}}',
          },
          { count: row.figure ?? 0 }
        );
      case UnpublishFactKind.analytics:
      default:
        return intl.formatMessage({
          id: 'app.sml.unpublish.fact.analytics.title',
          defaultMessage: 'Analytics are kept',
        });
    }
  };

  const detailFor = (row: UnpublishFactRow): string => {
    switch (row.kind) {
      case UnpublishFactKind.offline:
        return shareUrl ?? '';
      case UnpublishFactKind.address:
        return intl.formatMessage({
          id: 'app.sml.unpublish.fact.address.text',
          defaultMessage: 'A draft holds no address. Another page can claim this one while yours is offline.',
        });
      case UnpublishFactKind.presaves:
        return releaseDate === null
          ? intl.formatMessage({
              id: 'app.sml.unpublish.fact.presaves.text',
              defaultMessage: 'Fans who already signed up get the release. Nobody new can sign up.',
            })
          : intl.formatMessage(
              {
                id: 'app.sml.unpublish.fact.presaves.text_dated',
                defaultMessage:
                  'Fans who already signed up get the release on {date}. Nobody new can sign up while the page is down.',
              },
              { date: intl.formatDate(releaseDate, { day: 'numeric', month: 'long' }) }
            );
      case UnpublishFactKind.analytics:
      default:
        return intl.formatMessage({
          id: 'app.sml.unpublish.fact.analytics.text',
          defaultMessage: 'Nothing already recorded is deleted. Collection simply pauses.',
        });
    }
  };

  return (
    <Panel nested className={styles.card}>
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.unpublish.facts.head', defaultMessage: 'While it is offline' })}
      />
      {rows.map((row) => (
        <div key={row.kind} className={clsx(styles.row, row.isHard && styles.hard)}>
          <div className={styles.text}>
            <span className={clsx(styles.title, row.isHard && styles.titleHard)}>{titleFor(row)}</span>
            <span className={clsx(styles.detail, row.kind === UnpublishFactKind.offline && styles.mono)}>
              {detailFor(row)}
            </span>
          </div>
          {row.kind === UnpublishFactKind.analytics && row.figure !== null && (
            <span className={styles.figure}>{intl.formatNumber(row.figure)}</span>
          )}
        </div>
      ))}
    </Panel>
  );
};

export const SMLUnpublishList = TypedMemo(SMLUnpublishListComponent);
